/**
 * Lightweight haptic feedback helpers for native Capacitor builds.
 * No-ops on web/PWA where the Haptics plugin is unavailable.
 */

import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";

/**
 * Triggers a success notification haptic when a definitive good outcome is shown (e.g. booking succeeded).
 * Silently returns on web and when the native plugin rejects the call.
 *
 * @returns Resolves when the haptic call completes or is skipped.
 */
export const triggerSuccessHaptic = async(): Promise<void> => {
    if ( Capacitor.isNativePlatform() === false ) {
        return;
    }

    try {
        await Haptics.notification( { "type": NotificationType.Success } );
    } catch {
        // Haptics may be unavailable on some devices; confirmation UX must not fail.
    }
};

/**
 * Triggers a light impact haptic when the user changes a discrete selection (e.g. tab).
 * Silently returns on web and when the native plugin rejects the call.
 *
 * @returns Resolves when the haptic call completes or is skipped.
 */
export const triggerSelectionHaptic = async(): Promise<void> => {
    if ( Capacitor.isNativePlatform() === false ) {
        return;
    }

    try {
        await Haptics.impact( { "style": ImpactStyle.Light } );
    } catch {
        // Haptics may be unavailable on some devices; selection UX must not fail.
    }
};
